import { MediumLargeHeadingStyled } from "src/components/custom/texts/texts.styled";
import { Button, Flex } from "@nubeteck/components";
import { Card, DatePicker, Form, Select, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect } from "react";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";
import { _translateValue } from "../translation-gestor/translateValue";

const SCREEN_NAME = "PantallaCoursesProjectionsImport";

function CoursesProjectionsImport({
  isLoading,
  getData,
  data,
  periodosOptions,
  getExcel,
  isLoadingExcel,
}: any) {
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);
  const [periodoId, setPeriodoId] = React.useState<number>(0);
  const [anio, setAnio] = React.useState<number>();
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const [hasError, setHasError] = React.useState(false);
  const [translatedData, setTranslatedData] = React.useState<{
    asignaturas: { [key: string]: string };
  }>({
    asignaturas: {},
  });

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setImportDataPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  useEffect(() => {
    getData({
      rowsCount,
      page: pageNumber,
      periodoId,
      procesoId: 0,
      anio,
    });
  }, [rowsCount, pageNumber, getData, periodoId, anio]);

  // Efecto para traducir los campos traducibles cuando lleguen los datos o cambie el locale
  useEffect(() => {
    const translateData = async () => {
      // Verificar si hay datos para traducir
      const hasData = data?.data?.result && Array.isArray(data.data.result) && data.data.result.length > 0;

      // Si no hay datos, no hacer nada
      if (!hasData) {
        setTranslatedData({
          asignaturas: {},
        });
        return;
      }

      const asignaturasTranslations: { [key: string]: string | null } = {};
      const translationPromises: Promise<void>[] = [];

      // Recopilar todos los valores únicos que necesitan traducción
      data.data.result.forEach((record: any) => {
        // Traducir asignatura
        if (record.asignaturaNombre && !asignaturasTranslations.hasOwnProperty(record.asignaturaNombre)) {
          asignaturasTranslations[record.asignaturaNombre] = null; // Marcador para evitar duplicados
          translationPromises.push(
            _translateValue(SCREEN_NAME, record.asignaturaNombre, locale).then((translated) => {
              asignaturasTranslations[record.asignaturaNombre] = translated;
            })
          );
        }
      });

      await Promise.all(translationPromises);

      // Actualizar estados con las traducciones
      setTranslatedData({
        asignaturas: asignaturasTranslations as { [key: string]: string },
      });
    };

    translateData();
  }, [data?.data?.result, locale]);

  const columns = React.useMemo<ColumnsType<any>>(
    () => [
      {
        title: importDataPage?.subject_proyection_table  ?.[0]?.label || "Asignatura",
        dataIndex: "asignaturaNombre",
        render: (text: string) => translatedData.asignaturas[text] || text,
      },
      {
        title: importDataPage?.subject_proyection_table ?.[1]?.label || "Año",
        dataIndex: "anio",
      },
      {
        title: importDataPage?.subject_proyection_table ?.[2]?.label || "Periodo",
        dataIndex: "periodoNombre",
      },
      {
        title: importDataPage?.subject_proyection_table ?.[3]?.label || "Cantidad de estudiantes",
        dataIndex: "cantidadEstudiantes",
      },
    ],
    [importDataPage, translatedData],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setImportDataPage(null);
      }}
    />;
  }

  return (
    <>
      {isLoadingContent ? (
        <>
          <Skeleton.Input active size="large" style={{ width: 250, marginBottom: 16 }} />
          <Flex style={{ gap: "15px", justifyContent: "space-between", marginBottom: 16 }}>
            <Flex style={{ gap: "15px" }}>
              <Skeleton.Input active size="default" style={{ width: 200 }} />
              <Skeleton.Input active size="default" style={{ width: 200 }} />
            </Flex>
            <Skeleton.Input active size="default" style={{ width: 150 }} />
          </Flex>
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </>
      ) : (
        <>
          <MediumLargeHeadingStyled>{importDataPage?.subject_proyection.title || "Proyecciones de cursos importadas"}</MediumLargeHeadingStyled>
          <Flex style={{ gap: "15px", justifyContent: "space-between" }}>
            <Flex style={{ gap: "15px" }}>
              <Form.Item label={importDataPage?.subject_proyection.filter || "Filtro por periodo"}>
                <Select
                  options={periodosOptions}
                  placeholder={importDataPage?.subject_proyection.select_option || "Seleccione un periodo"}
                  allowClear
                  onChange={(e) => {
                    setPeriodoId(e);
                  }}
                />
              </Form.Item>
              <Form.Item label={importDataPage?.subject_proyection.filterpage_B || "Filtro por año"}>
                <DatePicker
                  picker="year"
                  onChange={(e) => {
                    setAnio(e?.toDate().getFullYear());
                  }}
                  placeholder={importDataPage?.subject_proyection.year || "Año"}
                />
              </Form.Item>
            </Flex>
            <Button
              type="primary"
              onClick={() => getExcel(anio ?? 0, periodoId ?? 0)}
              loading={isLoadingExcel}
            >{`${importDataPage?.subject_proyection.export_button || "Exportar a excel"} ${
              anio && periodoId > 0
                ? "(año y periodo seleccionado)"
                : anio
                ? "(año seleccionado)"
                : periodoId > 0
                ? "(periodo seleccionado)"
                : ""
            }`}</Button>
          </Flex>
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Table<any>
              loading={isLoading}
              columns={columns}
              dataSource={data?.data?.result}
              rowKey={(v) => v.asignaturaId}
              pagination={{
                onChange(page, pageSize) {
                  setPageNumber(page);
                  setRowsCount(pageSize);
                },
                pageSize: rowsCount,
                current: pageNumber,
                total: data?.data?.pageCount * rowsCount,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      )}
    </>
  );
}

export default CoursesProjectionsImport;
